import { useCluster } from '@cast/shared-cluster-context-data-access';
import { formatCurrency, getCurrencyPrecision, toNumOrZero } from '@cast/utils';

import { getMonthlySavingsFromOptimization } from '@cast/features-cluster-shared-utils';

import { useClusterOptimizationsQuery } from './useClusterOptimizationsQuery';

export type OptimizeBannerMetrics = {
  utilizationGainPct?: number;
  recoverableSpend?: number;
  recoverableSpendLabel: string;
  safeConsolidationCount?: number;
  hasMetrics: boolean;
  isMetricsLoading: boolean;
};

/**
 * Cluster optimization metrics shared by Optimize banner and
 * StartAutomationTrialBanner (paid reporting → PLG upsell).
 */
export const useOptimizeBannerMetrics = (): OptimizeBannerMetrics => {
  const { cluster } = useCluster();

  const { data: optimizations, isLoading } = useClusterOptimizationsQuery({
    clusterIds: [cluster.id],
  });

  const optimization = optimizations?.find(
    (item) => item.clusterId === cluster.id
  );

  const recoverableSpend = getMonthlySavingsFromOptimization(optimization);

  const overprovisionValues = [
    optimization?.efficiency?.cpuOverprovisionPercent,
    optimization?.efficiency?.memoryOverprovisionPercent,
  ].filter((value): value is number => value != null && value > 0);

  const utilizationGainPct = overprovisionValues.length
    ? Math.round(
        overprovisionValues.reduce((sum, value) => sum + value, 0) /
          overprovisionValues.length
      )
    : undefined;

  const safeConsolidationCount =
    toNumOrZero(optimization?.infrastructureAutoscaler?.issueCount) +
    toNumOrZero(optimization?.workloadAutoscaler?.issueCount);

  const recoverableSpendLabel =
    recoverableSpend > 0
      ? formatCurrency(recoverableSpend, {
          maximumFractionDigits: getCurrencyPrecision(recoverableSpend),
        })
      : '—';

  const hasMetrics =
    utilizationGainPct != null ||
    recoverableSpend > 0 ||
    safeConsolidationCount > 0;

  return {
    utilizationGainPct,
    recoverableSpend: recoverableSpend > 0 ? recoverableSpend : undefined,
    recoverableSpendLabel,
    safeConsolidationCount:
      safeConsolidationCount > 0 ? safeConsolidationCount : undefined,
    hasMetrics,
    isMetricsLoading: isLoading,
  };
};
